import { useMemo } from "react";
import { useAccess } from "../access/api";
import { useBillingState } from "../bridges/frame";
import { NewsItem, Topic, WHOLE_TOUR } from "./content";
import { newsFor, topicsFor, tourVisible, Who } from "./visibility";
import { useHelpState, useMarkSeen } from "./api";

const EMPTY: ReadonlySet<string> = new Set();

/**
 * Everything the Help page needs about this person (one place, so the page, the menu dot and the links agree):
 *  - who they are for Help (switches, role, team), from the access answer;
 *  - a locked account: Contact us only, so no topics, no items, no request for what was opened (H1.2);
 *  - what they have opened and the Start-here facts.
 * `ready` is false while the access answer is still coming (the page shows its placeholder, never a flash of "nothing for you").
 */
export const useHelp = (opts: { enabled?: boolean } = {}) => {
  const enabled = opts.enabled ?? true;
  const access = useAccess();
  const { locked } = useBillingState();
  const state = useHelpState(enabled && !locked);
  const mark = useMarkSeen();
  // A failed refresh makes an old answer untrustworthy (as useCan): nothing is shown from it.
  const data = access.error ? undefined : access.data;
  const who: Who | null = useMemo(() => (data ? { permissions: data.permissions, role: data.role, teamAllowed: data.workspace.teamAllowed } : null), [data]);

  const topics: Topic[] = useMemo(() => (who && !locked ? topicsFor(who) : []), [who, locked]);
  const tour: Topic | null = who && !locked && tourVisible(who) ? WHOLE_TOUR : null;
  const news: NewsItem[] = useMemo(() => (who && !locked ? newsFor(who) : []), [who, locked]);
  const seenItems = state.data?.items ?? EMPTY;
  const seenTopics = state.data?.topics ?? EMPTY;
  const newCount = state.data ? news.filter((n) => !seenItems.has(n.id)).length : 0;

  return {
    ready: !!who || !!access.error,
    failed: !!access.error && !who,
    locked,
    who,
    topics, tour, news,
    seenItems, seenTopics,
    /** Unopened items this person may see; 0 until the state has loaded (no dot, no label, rather than a wrong one). */
    newCount,
    stateReady: !!state.data,
    stateFailed: !!state.error && !state.data,
    refetchState: () => { void state.refetch(); },
    startHere: state.data?.startHere ?? null,
    markItem: (id: string) => mark("item", id),
    markTopic: (id: string) => mark("topic", id),
  };
};

/** The quiet dot on the Help menu item. Light on purpose: only the access answer and the opened list; today's Help never asks. */
export const useHelpDot = (enabled: boolean): boolean => {
  const h = useHelp({ enabled });
  return enabled && h.newCount > 0;
};
